/**
 * App Config Context
 *
 * Manages app-wide configuration including:
 * - Primary/secondary colors from API
 * - Font family
 * - Other configurable settings
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  applyPrimaryColor,
  applySecondaryColor,
  applyFontFamily,
  isValidHex,
} from '../utils/colorUtils';
import { initViewportHeight } from '../utils/responsiveUtils';

const AppConfigContext = createContext(undefined);

// Default configuration
const DEFAULT_CONFIG = {
  primaryColor: '#FB5850',
  secondaryColor: '#572148',
  fontFamily: 'Prompt',
};

export const AppConfigProvider = ({ children, initialConfig = {} }) => {
  const [config, setConfigState] = useState({
    ...DEFAULT_CONFIG,
    ...initialConfig,
  });
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize viewport height fix
  useEffect(() => {
    const cleanup = initViewportHeight();
    return cleanup;
  }, []);

  // Apply configuration to CSS variables
  useEffect(() => {
    if (config.primaryColor && isValidHex(config.primaryColor)) {
      applyPrimaryColor(config.primaryColor);
    }

    if (config.secondaryColor && isValidHex(config.secondaryColor)) {
      applySecondaryColor(config.secondaryColor);
    }

    if (config.fontFamily) {
      applyFontFamily(config.fontFamily);
    }

    setIsInitialized(true);
  }, [config]);

  /**
   * Update configuration
   * @param {Partial<typeof DEFAULT_CONFIG>} newConfig
   */
  const setConfig = useCallback((newConfig) => {
    setConfigState((prev) => ({
      ...prev,
      ...newConfig,
    }));
  }, []);

  /**
   * Update primary color
   * @param {string} color - Hex color code
   */
  const setPrimaryColor = useCallback((color) => {
    if (isValidHex(color)) {
      setConfigState((prev) => ({
        ...prev,
        primaryColor: color,
      }));
    }
  }, []);

  /**
   * Update secondary color
   * @param {string} color - Hex color code
   */
  const setSecondaryColor = useCallback((color) => {
    if (isValidHex(color)) {
      setConfigState((prev) => ({
        ...prev,
        secondaryColor: color,
      }));
    }
  }, []);

  /**
   * Update font family
   * @param {string} font - Font family name
   */
  const setFontFamily = useCallback((font) => {
    if (font && typeof font === 'string') {
      setConfigState((prev) => ({
        ...prev,
        fontFamily: font,
      }));
    }
  }, []);

  /**
   * Apply configuration from event/sponsor API response
   * Maps API fields to config
   * @param {Object} apiData - API response data
   */
  const applyFromApi = useCallback((apiData) => {
    const newConfig = {};

    // Map possible API field names for primary color
    if (apiData.att_app_pri_col) {
      newConfig.primaryColor = apiData.att_app_pri_col;
    } else if (apiData.primary_color) {
      newConfig.primaryColor = apiData.primary_color;
    } else if (apiData.primaryColor) {
      newConfig.primaryColor = apiData.primaryColor;
    } else if (apiData.sponsor_pri_color) {
      newConfig.primaryColor = apiData.sponsor_pri_color;
    }

    // Map possible API field names for secondary color
    if (apiData.att_app_sec_col) {
      newConfig.secondaryColor = apiData.att_app_sec_col;
    } else if (apiData.secondary_color) {
      newConfig.secondaryColor = apiData.secondary_color;
    } else if (apiData.secondaryColor) {
      newConfig.secondaryColor = apiData.secondaryColor;
    }

    // Map possible API field names for font
    if (apiData.att_app_font) {
      newConfig.fontFamily = apiData.att_app_font;
    } else if (apiData.font_family) {
      newConfig.fontFamily = apiData.font_family;
    } else if (apiData.fontFamily) {
      newConfig.fontFamily = apiData.fontFamily;
    }

    if (Object.keys(newConfig).length > 0) {
      setConfig(newConfig);
    }
  }, [setConfig]);

  /**
   * Reset to default configuration
   */
  const resetConfig = useCallback(() => {
    setConfigState(DEFAULT_CONFIG);
  }, []);

  const value = {
    config,
    setConfig,
    setPrimaryColor,
    setSecondaryColor,
    setFontFamily,
    applyFromApi,
    resetConfig,
    isInitialized,
  };

  return (
    <AppConfigContext.Provider value={value}>
      {children}
    </AppConfigContext.Provider>
  );
};

/**
 * Hook to access app config context
 */
export const useAppConfig = () => {
  const context = useContext(AppConfigContext);
  if (context === undefined) {
    throw new Error('useAppConfig must be used within an AppConfigProvider');
  }
  return context;
};

export default AppConfigContext;
